import { useEffect, useRef, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import Swiper from "swiper/bundle";
import toast from "react-hot-toast";
import { endpoint } from "../../api/endpoint";
import { apiHandler } from "../../api";
import { useProductUpload } from "./useProductUpload";
import {
  deletePlaygroundItem,
  getPlaygroundItemLabel,
  resolvePlaygroundItemId,
  showDeleteConfirmationToast,
} from "../../utils/playgroundDelete";

// Kick off image downloads immediately, before any component renders
function prefetchImages(urls) {
  if (!Array.isArray(urls)) return;

  urls.forEach((url) => {
    if (!url) return;
    const img = new Image();
    img.src = url;
  });
}

const DEFAULT_TOP_TITLE = "Top";
const DEFAULT_TRENDY_TITLE = "Trendy";
const productsCache = new Map();

export const invalidateProductsCache = () => {
  productsCache.clear();
};

function normalizeProductItem(item) {
  const activeFlag =
    item?.bIsActive !== undefined
      ? Boolean(item.bIsActive && item.bIsActive !== "0" && item.bIsActive !== 0)
      : item?.isActive !== undefined
        ? Boolean(item.isActive && item.isActive !== "0" && item.isActive !== 0)
        : item?.status !== undefined
          ? Boolean(item.status === true || item.status === 1 || item.status === "1" || item.status === "active")
          : true;

  return {
    ...item,
    bIsActive: activeFlag,
    isActive: activeFlag,
  };
}

function normalizeProductCollections(rawData, isUserRole = false) {
  if (!rawData || typeof rawData !== "object") {
    return {
      collections: [],
      topTitle: DEFAULT_TOP_TITLE,
      trendyTitle: DEFAULT_TRENDY_TITLE,
      top: [],
      trendy: [],
    };
  }

  let entries = [];

  // Focus on rawData.collections first to map all collection categories
  if (rawData.collections && typeof rawData.collections === "object") {
    if (Array.isArray(rawData.collections)) {
      entries = rawData.collections.map((item, idx) => [
        item.title || item.collectionName || item.name || `Collection ${idx + 1}`,
        Array.isArray(item.products) ? item.products : Array.isArray(item.items) ? item.items : [],
      ]);
    } else {
      entries = Object.entries(rawData.collections).filter(([, value]) => Array.isArray(value));
    }
  } else {
    // Fallback to rawData root keys if collections is missing
    entries = Object.entries(rawData).filter(
      ([key, value]) =>
        Array.isArray(value) &&
        !["rows", "filters", "categories", "filterCategories"].includes(key)
    );
  }

  const collections = entries.map(([title, list]) => {
    let normalizedItems = Array.isArray(list)
      ? list.map((item) => ({
          ...normalizeProductItem(item),
          collectionTitle: item.collectionTitle || item.collectionName || title,
          collectionName: item.collectionName || item.collectionTitle || title,
        }))
      : [];
    if (isUserRole) {
      normalizedItems = normalizedItems.filter((p) => p.bIsActive);
    }
    return {
      id: title,
      title: title,
      products: normalizedItems,
    };
  });

  const topEntry = collections[0];
  const trendyEntry = collections[1];

  return {
    collections,
    topTitle: topEntry?.title || DEFAULT_TOP_TITLE,
    trendyTitle: trendyEntry?.title || DEFAULT_TRENDY_TITLE,
    top: topEntry?.products || [],
    trendy: trendyEntry?.products || [],
  };
}

const getInitialCategory = (selectedProduct) =>
  selectedProduct?.category === "women" || selectedProduct?.category === "men"
    ? selectedProduct.category
    : "women";

const getInitialModelType = (selectedProduct) =>
  selectedProduct?.modelType === "model" || selectedProduct?.modelType === "mannequin"
    ? selectedProduct.modelType
    : "model";

const getMappedModelType = (modelType) =>
  modelType === "mannequin" ? "mannequin_to_model" : "model_to_model";

const getCachedProducts = (token, category, modelType, page = 1, limit = 2) => {
  const cacheKey = `${token || "guest"}-${category}-${getMappedModelType(modelType)}-${page}-${limit}`;
  return productsCache.get(cacheKey) || null;
};

export const useProductSelector = ({
  setSelectedProduct,
  setStep,
  selectedProduct,
  modelsData = {},
  backgroundsData = {},
}) => {
  const { role: userRole, token: reduxToken } = useSelector((state) => state.auth || {});
  const token =
    reduxToken ||
    (typeof sessionStorage !== "undefined"
      ? sessionStorage.getItem("accessToken")
      : null);
  const isUserRole = userRole === "user" || userRole !== "admin";

  const topRailRef = useRef(null);
  const bottomRailRef = useRef(null);
  const topRef = useRef(null);
  const bottomRef = useRef(null);
  const innerRefs = useRef([]);
  const nextRefs = useRef([]);
  const prevRefs = useRef([]);

  // Modal States
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [isBulkImageUploadModalOpen, setIsBulkImageUploadModalOpen] = useState(false);

  // Zoom Modal State
  const [zoomModal, setZoomModal] = useState({ front: "", back: "" });
  const [isMobileZoomOpen, setIsMobileZoomOpen] = useState(false);

  // Rail Scroll States
  const [topScroll, setTopScroll] = useState({ canScrollLeft: false, canScrollRight: false });
  const [bottomScroll, setBottomScroll] = useState({ canScrollLeft: false, canScrollRight: false });

  const initialCategory = getInitialCategory(selectedProduct);
  const initialModelType = getInitialModelType(selectedProduct);
  const initialCachedProducts = getCachedProducts(token, initialCategory, initialModelType, 1, 2);

  const [category, setCategory] = useState(initialCategory);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [filters, setFilters] = useState({});
  const [apiFilters, setApiFilters] = useState([]);
  const [modelType, setModelType] = useState(initialModelType);

  // Pagination State
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(2);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [sectionTitles, setSectionTitles] = useState({
    topTitle: initialCachedProducts?.topTitle || DEFAULT_TOP_TITLE,
    trendyTitle: initialCachedProducts?.trendyTitle || DEFAULT_TRENDY_TITLE,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [filteredCollections, setFilteredCollections] = useState(
    initialCachedProducts?.collections || []
  );
  const [filteredTop, setFilteredTop] = useState(initialCachedProducts?.top || []);
  const [filteredTrendy, setFilteredTrendy] = useState(initialCachedProducts?.trendy || []);
  const mappedModelType = getMappedModelType(modelType);

  const {
    uploadProduct,
    updateProduct,
    fetchFilterCategories,
    fetchCollectionCategories,
    isLoading: isProductUploadLoading,
  } = useProductUpload();

  // Reset page when category or modelType changes
  useEffect(() => {
    setPage(1);
  }, [category, modelType]);

  const fetchProducts = useCallback(
    async ({ forceRefresh = false } = {}) => {
      const activeToken =
        token ||
        (typeof sessionStorage !== "undefined"
          ? sessionStorage.getItem("accessToken")
          : null);

      if (!activeToken) {
        return;
      }

      const cacheKey = `${activeToken || "guest"}-${category}-${mappedModelType}-${page}-${limit}`;

      if (forceRefresh) {
        productsCache.delete(cacheKey);
      }

      const cachedData = productsCache.get(cacheKey);
      if (cachedData && !forceRefresh) {
        setFilteredCollections(cachedData.collections || []);
        setFilteredTop(cachedData.top || []);
        setFilteredTrendy(cachedData.trendy || []);
        setSectionTitles({ topTitle: cachedData.topTitle, trendyTitle: cachedData.trendyTitle });
        setTotal(cachedData.total || 0);
        setTotalPages(cachedData.totalPages || 1);
        if (cachedData.filters) {
          setApiFilters(cachedData.filters);
        }
        setError(null);
      }

      let url = `${endpoint.API_BASE_URL}${endpoint.GET_PRODUCTS}?gender=${category}&mode=${mappedModelType}&page=${page}&limit=${limit}`;

      // Dynamic search query param
      if (debouncedSearchTerm && debouncedSearchTerm.trim()) {
        url += `&search=${encodeURIComponent(debouncedSearchTerm.trim())}`;
      }

      // Dynamic collection filter query param if selected
      const rawCollection = filters?.collection ?? filters?.collections ?? [];
      const selectedCollection = Array.isArray(rawCollection)
        ? rawCollection[0]
        : typeof rawCollection === "string"
          ? rawCollection
          : null;
      if (
        selectedCollection &&
        selectedCollection.trim() &&
        selectedCollection.trim().toLowerCase() !== "all"
      ) {
        url += `&collection=${encodeURIComponent(selectedCollection.trim())}`;
      }

      // Dynamic category filter query param if selected
      const rawCategory = filters?.category ?? filters?.categories ?? [];
      const selectedCategory = Array.isArray(rawCategory)
        ? rawCategory[0]
        : typeof rawCategory === "string"
          ? rawCategory
          : null;
      if (
        selectedCategory &&
        selectedCategory.trim() &&
        selectedCategory.trim().toLowerCase() !== "all"
      ) {
        url += `&category=${encodeURIComponent(selectedCategory.trim())}`;
      }

      // Dynamic price filter query param if selected
      const rawPrice = filters?.price ?? [];
      const selectedPrice = Array.isArray(rawPrice)
        ? rawPrice[0]
        : typeof rawPrice === "string"
          ? rawPrice
          : null;
      if (
        selectedPrice &&
        selectedPrice.trim() &&
        selectedPrice.trim().toLowerCase() !== "all"
      ) {
        url += `&price=${encodeURIComponent(selectedPrice.trim())}`;
      }

      try {
        if (!cachedData) {
          setLoading(true);
        }
        setError(null);
        const response = await apiHandler({
          url,
          method: "GET",
          authToken: { token: activeToken },
          noCache: true,
        });
        const data = response?.data?.data;
        const normalized = normalizeProductCollections(data, isUserRole);

        const total =
          Number(
            data?.total ??
              response?.data?.total ??
              data?.totalCount ??
              response?.data?.totalCount ??
              data?.pagination?.total ??
              response?.data?.pagination?.total
          ) || 0;

        const totalPages =
          Number(
            data?.totalPages ??
              response?.data?.totalPages ??
              data?.pagination?.totalPages ??
              response?.data?.pagination?.totalPages
          ) || (total > 0 && limit > 0 ? Math.ceil(total / limit) : 1);

        const returnedFilters = data?.filters || response?.data?.filters || null;

        const validCollections = (normalized.collections || []).filter(
          (c) => Array.isArray(c.products) && c.products.length > 0
        );

        setFilteredCollections(validCollections);
        setFilteredTop(normalized.top);
        setFilteredTrendy(normalized.trendy);
        setSectionTitles({ topTitle: normalized.topTitle, trendyTitle: normalized.trendyTitle });
        setTotal(total);
        setTotalPages(totalPages);
        if (Array.isArray(returnedFilters) && returnedFilters.length > 0) {
          setApiFilters((prev) =>
            prev.length > 0 && returnedFilters.length < prev.length ? prev : returnedFilters
          );
        }

        productsCache.set(cacheKey, {
          collections: validCollections,
          top: normalized.top,
          trendy: normalized.trendy,
          topTitle: normalized.topTitle,
          trendyTitle: normalized.trendyTitle,
          total,
          totalPages,
          filters: returnedFilters,
        });
        setError(null);
      } catch (err) {
        setError(err.message);
        setFilteredCollections([]);
        setFilteredTop([]);
        setFilteredTrendy([]);
        setSectionTitles({
          topTitle: DEFAULT_TOP_TITLE,
          trendyTitle: DEFAULT_TRENDY_TITLE,
        });
        setTotal(0);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    },
    [token, category, mappedModelType, page, limit, isUserRole, debouncedSearchTerm, filters]
  );

  // Fetch products from API
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const refreshProducts = useCallback(() => {
    fetchProducts({ forceRefresh: true });
  }, [fetchProducts]);

  const updateProductStatus = useCallback((itemId, newIsActive) => {
    const isBoolActive = Boolean(
      newIsActive !== undefined && newIsActive !== null
        ? newIsActive && newIsActive !== "0" && newIsActive !== 0
        : false
    );

    invalidateProductsCache();

    const updateItem = (item) => {
      const id = resolvePlaygroundItemId(item);
      if (String(id) === String(itemId)) {
        return {
          ...item,
          bIsActive: isBoolActive,
          isActive: isBoolActive,
        };
      }
      return item;
    };

    setFilteredCollections((prev) =>
      (prev || []).map((col) => ({
        ...col,
        products: (col.products || []).map(updateItem),
      }))
    );
    setFilteredTop((prev) => (Array.isArray(prev) ? prev.map(updateItem) : []));
    setFilteredTrendy((prev) => (Array.isArray(prev) ? prev.map(updateItem) : []));
  }, []);

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  const onSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  const onCategoryChange = (eOrValue) => {
    const newCat = typeof eOrValue === "string" ? eOrValue : eOrValue?.target?.value;
    if (!newCat) return;
    setCategory(newCat);
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setFilters({});
    setPage(1);
  };

  const onFilterChange = (filterObj) => {
    setFilters(filterObj || {});
    setPage(1);
  };

  const onModelTypeChange = (eOrValue) => {
    const nextModelType = typeof eOrValue === "string" ? eOrValue : eOrValue?.target?.value;
    if (!nextModelType) return;
    setModelType(nextModelType);
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setFilters({});
    setPage(1);
  };

  // Reset page when debounced search term changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  // Eagerly prefetch front images
  useEffect(() => {
    const allVisible = (filteredCollections || []).flatMap((c) => c.products || []);
    const urls = allVisible.slice(0, 10).map((p) => p.images?.[0]).filter(Boolean);
    prefetchImages(urls);
  }, [filteredCollections]);

  useEffect(() => {
    const breakpoints = {
      320: { slidesPerView: 1.2, spaceBetween: 15 },
      480: { slidesPerView: 2.5, spaceBetween: 15 },
      640: { slidesPerView: 4, spaceBetween: 15 },
    };

    const topSwiper = new Swiper(topRef.current, {
      slidesPerView: 4,
      spaceBetween: 10,
      breakpoints,
      navigation: {
        nextEl: ".top-next",
        prevEl: ".top-prev",
      },
    });

    const bottomSwiper = new Swiper(bottomRef.current, {
      slidesPerView: 4,
      spaceBetween: 10,
      breakpoints,
      navigation: {
        nextEl: ".trendy-next",
        prevEl: ".trendy-prev",
      },
    });

    const innerSwipers = innerRefs.current.map((el, i) => {
      if (!el) return null;
      return new Swiper(el, {
        slidesPerView: 1,
        navigation: {
          nextEl: nextRefs.current[i],
          prevEl: prevRefs.current[i],
        },
      });
    });

    return () => {
      topSwiper.destroy(true, true);
      bottomSwiper.destroy(true, true);
      innerSwipers.forEach((s) => s?.destroy(true, true));
    };
  }, [filteredTop, filteredTrendy]);

  // Rail scroll logic
  const updateScrollState = (railRef, setScrollState) => {
    if (railRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = railRef.current;
      const canScrollLeft = scrollLeft > 2;
      const canScrollRight = scrollLeft + clientWidth < scrollWidth - 2;
      setScrollState({ canScrollLeft, canScrollRight });
    }
  };

  useEffect(() => {
    const topEl = topRailRef.current;
    const bottomEl = bottomRailRef.current;

    const handleTopScroll = () => updateScrollState(topRailRef, setTopScroll);
    const handleBottomScroll = () => updateScrollState(bottomRailRef, setBottomScroll);

    const timer = setTimeout(() => {
      if (topEl) updateScrollState(topRailRef, setTopScroll);
      if (bottomEl) updateScrollState(bottomRailRef, setBottomScroll);
    }, 150);

    if (topEl) {
      topEl.addEventListener("scroll", handleTopScroll);
    }
    if (bottomEl) {
      bottomEl.addEventListener("scroll", handleBottomScroll);
    }

    return () => {
      clearTimeout(timer);
      if (topEl) topEl.removeEventListener("scroll", handleTopScroll);
      if (bottomEl) bottomEl.removeEventListener("scroll", handleBottomScroll);
    };
  }, [filteredTop, filteredTrendy]);

  const scrollRail = (railRef, direction) => {
    if (railRef.current) {
      const scrollAmount = railRef.current.clientWidth * 0.8;
      railRef.current.scrollBy({ left: direction * scrollAmount, behavior: "smooth" });
    }
  };

  // Zoom modal actions
  const openZoomModal = (front, back = "") => {
    setZoomModal({ front, back });
    if (window.innerWidth < 768) {
      setIsMobileZoomOpen(true);
    }
  };

  const closeZoomModal = () => {
    setZoomModal({ front: "", back: "" });
    setIsMobileZoomOpen(false);
  };

  // Modal actions
  const handleUploadModalOpen = () => {
    setEditingProduct(null);
    setIsUploadModalOpen(true);
  };

  const handleEditModalOpen = (product) => {
    const enrichedProduct = product
      ? {
          ...product,
          gender: product.gender || product.genderName || product.sGender || category,
          mode: product.mode || product.modeName || product.sMode || product.modelType || mappedModelType,
        }
      : product;
    setEditingProduct(enrichedProduct);
    setIsUploadModalOpen(true);
  };

  const handleUploadModalClose = () => {
    setIsUploadModalOpen(false);
    setEditingProduct(null);
  };

  const handleBulkUploadModalOpen = () => {
    setIsBulkUploadModalOpen(true);
  };

  const handleBulkUploadModalClose = () => {
    setIsBulkUploadModalOpen(false);
  };

  const handleBulkImageUploadModalOpen = () => {
    setIsBulkImageUploadModalOpen(true);
  };

  const handleBulkImageUploadModalClose = () => {
    setIsBulkImageUploadModalOpen(false);
    refreshProducts();
  };

  const handleUploadSubmit = async (payload, productToEdit) => {
    try {
      if (productToEdit) {
        const productId = resolvePlaygroundItemId(productToEdit) || productToEdit.id || productToEdit.iProductId;
        const result = await updateProduct(productId, payload);
        if (result.success) {
          toast.success("Product updated successfully!");
          handleUploadModalClose();
          refreshProducts();
          return result;
        } else {
          toast.error(`Update failed: ${result.error}`);
          return result;
        }
      } else {
        const result = await uploadProduct(payload);
        if (result.success) {
          toast.success("Product uploaded successfully!");
          handleUploadModalClose();
          refreshProducts();
          return result;
        } else {
          toast.error(`Update failed: ${result.error}`);
          return result;
        }
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
      return { success: false, error: error.message };
    }
  };

  const getGenerationCount = (product) => {
    const count = product?.generationCount ?? 0;
    const numericCount = Number(count);
    return Number.isFinite(numericCount) && numericCount >= 0
      ? numericCount
      : 0;
  };

  const handleDeleteProduct = (product) => {
    const itemId = resolvePlaygroundItemId(product);
    if (!itemId) {
      toast.error("This product cannot be deleted because it is missing an id.");
      return;
    }

    showDeleteConfirmationToast({
      title: "Delete product?",
      description: `This will permanently remove ${getPlaygroundItemLabel(product)} from the playground.`,
      confirmLabel: "Delete product",
      onConfirm: async () => {
        try {
          const result = await deletePlaygroundItem({
            resource: "product",
            itemId,
            token,
          });

          if (result?.status >= 200 && result?.status < 300) {
            toast.success("Product deleted successfully.");
            if (
              selectedProduct &&
              resolvePlaygroundItemId(selectedProduct) === itemId
            ) {
              setSelectedProduct(null);
            }
            refreshProducts();
            return;
          }
          toast.error(
            result?.data?.error_message ||
              result?.data?.error ||
              "Failed to delete product."
          );
        } catch (error) {
          toast.error(error?.message || "Failed to delete product.");
        }
      },
    });
  };

  const handleToggleProduct = async (product) => {
    const itemId = resolvePlaygroundItemId(product);
    if (!itemId) return;
    const previousState = Boolean(product.bIsActive ?? product.isActive ?? true);
    const newState = !previousState;

    updateProductStatus(itemId, newState);

    try {
      const response = await apiHandler({
        url: `${endpoint.TOGGLE_PRODUCT}/${itemId}/toggle`,
        method: "PATCH",
        authToken: { token },
        noCache: true,
      });

      if (
        (response?.status && response.status >= 400) ||
        response?.data?.status === "error" ||
        response?.data?.success === false
      ) {
        throw new Error(response?.data?.message || "Failed to update product status");
      }

      const resData = response?.data?.data;
      const finalStatus =
        resData?.bIsActive !== undefined
          ? resData.bIsActive
          : resData?.isActive !== undefined
            ? resData.isActive
            : newState;

      updateProductStatus(itemId, finalStatus);

      toast.success(
        response?.data?.message ||
          (finalStatus ? "Product enabled" : "Product disabled")
      );
    } catch (err) {
      updateProductStatus(itemId, previousState);
      toast.error(err?.message || "Failed to update product status");
    }
  };

  const handleProductCardClick = (product) => {
    setSelectedProduct({
      ...product,
      category,
      modelType,
    });
  };

  const handleContinue = () => {
    if (selectedProduct) {
      setStep(2);
    }
  };

  return {
    userRole,
    category,
    onCategoryChange,
    searchTerm,
    onSearchChange,
    collections: filteredCollections,
    filteredCollections,
    filteredTop,
    filteredTrendy,
    onFilterChange,
    apiFilters,
    topSectionTitle: sectionTitles.topTitle,
    trendySectionTitle: sectionTitles.trendyTitle,
    modelType,
    mappedModelType,
    onModelTypeChange,
    refreshProducts,
    updateProductStatus,
    loading,
    pagination: {
      page,
      limit,
      total,
      totalPages,
    },
    onPageChange: setPage,
    getGenerationCount,

    // Refs
    topRailRef,
    bottomRailRef,
    topRef,
    bottomRef,
    innerRefs,
    nextRefs,
    prevRefs,

    // Scroll states & actions
    topScroll,
    bottomScroll,
    scrollRail,

    // Selection & Navigation
    handleProductCardClick,
    handleContinue,

    // Modals
    isUploadModalOpen,
    editingProduct,
    isBulkUploadModalOpen,
    isBulkImageUploadModalOpen,
    isProductUploadLoading,
    handleUploadModalOpen,
    handleEditModalOpen,
    handleUploadModalClose,
    handleBulkUploadModalOpen,
    handleBulkUploadModalClose,
    handleBulkImageUploadModalOpen,
    handleBulkImageUploadModalClose,
    handleUploadSubmit,
    fetchFilterCategories,
    fetchCollectionCategories,

    // Zoom modal
    zoomModal,
    isMobileZoomOpen,
    openZoomModal,
    closeZoomModal,

    // Admin handlers
    handleDeleteProduct,
    handleToggleProduct,
  };
};
